import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Check, EyeOff, GitMerge, RefreshCw, Scissors, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CategoryBadge, SourceBadges } from '../components/ConceptBadges'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { WeightLabel } from '../components/ArtworkTile'
import {
  useCurateMany, useDismiss, useRecordRule, useRefreshReview, useReviewItems, useReviewSummary, useSuggestCategories,
} from '../hooks/useKnowledge'
import { CATEGORIES, type ReviewItem, type ReviewKind } from '../api/knowledgeApi'

const PAGE = 50

const KINDS: Array<{ kind: ReviewKind; label: string; what: string; fix: string }> = [
  {
    kind: 'generic',
    label: 'Too generic',
    what: 'On a large share of all artworks, so it says little about any one of them.',
    fix: 'Mark it generic (it stays, but barely counts in search) or hide it. Remove it only if it is not a real concept at all.',
  },
  {
    kind: 'suspect_merge',
    label: 'Suspect merges',
    what: 'One concept whose names disagree: opposite words (“High-key” with “Low-key”) or names that share no wording.',
    fix: 'Tick the names that do not belong and split them off. The next build keeps them apart for good.',
  },
  {
    kind: 'duplicate',
    label: 'Likely duplicates',
    what: 'Two concepts that look like one: near-identical meaning (similarity ≥ 0.9) or the same name.',
    fix: 'Merge them if they mean the same thing. Dismiss if they are genuinely different.',
  },
  {
    kind: 'uncategorised',
    label: 'Uncategorised',
    what: 'No category — every concept from the 2024 graph. Categories drive the colours, filters and search facets.',
    fix: 'Ask for suggestions, then accept them (in bulk is fine) or pick another category.',
  },
  {
    kind: 'thin',
    label: 'Thin',
    what: 'A new-enrichment concept on a single artwork. Usually noise, and rarity gives it full weight in search.',
    fix: 'Hide the junk; leave the ones that are real but rare.',
  },
]

type Selection = Set<string>

function SelectBox({ id, selected, onToggle }: { id: string; selected: Selection; onToggle: (id: string) => void }) {
  return <Checkbox checked={selected.has(id)} onCheckedChange={() => onToggle(id)} aria-label="Select" />
}

function ConceptCell({ c }: { c: ReviewItem['concept'] }) {
  return (
    <div className="min-w-0">
      <Link to={`/app/knowledge/${c.ucid}`} className="font-medium hover:underline">{c.name}</Link>
      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <CategoryBadge category={c.category} />
        <SourceBadges sources={c.sources} />
        <span>{c.artworkCount.toLocaleString()} artworks</span>
      </div>
    </div>
  )
}

function SuspectRow({ item }: { item: ReviewItem }) {
  const record = useRecordRule()
  const dismiss = useDismiss()
  const [split, setSplit] = useState<string[]>([])
  const oddWords = new Set((item.detail.opposites ?? []).flatMap((o) => o.words))
  const names = item.concept.memberNames
  // One inline span per name, so the label's flex gap does not space out the words.
  const highlight = (name: string) => (
    <span>
      {name.split(/(\s+|-)/).map((part, i) =>
        oddWords.has(part.toLowerCase()) ? <mark key={i} className="rounded bg-amber-100">{part}</mark> : part
      )}
    </span>
  )
  const pairs = [...new Set((item.detail.opposites ?? []).map((o) => o.words.join(' / ')))]
  return (
    <div className="space-y-2 border-b py-3 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <ConceptCell c={item.concept} />
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            disabled={!split.length || split.length === names.length || record.isPending}
            onClick={() => record.mutate({ action: 'separate', ucid: item.concept.ucid, names: split })}
          >
            <Scissors className="mr-1.5 h-3.5 w-3.5" /> Split {split.length || ''} off
          </Button>
          <Button size="sm" variant="outline" onClick={() => dismiss.mutate([{ kind: item.kind, ucid: item.concept.ucid }])}>
            <Check className="mr-1.5 h-3.5 w-3.5" /> Looks right
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {names.map((n) => (
          <label key={n} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${split.includes(n) ? 'border-rose-300 bg-rose-50' : 'bg-white'}`}>
            <Checkbox checked={split.includes(n)} onCheckedChange={(v) => setSplit(v ? [...split, n] : split.filter((x) => x !== n))} />
            {highlight(n)}
          </label>
        ))}
      </div>
      <div className="text-xs text-slate-500">
        {pairs.length ? `Opposite words: ${pairs.join(', ')}. ` : ''}
        {item.detail.unrelated ? `“${item.detail.unrelated}” shares little wording with the others.` : ''}
      </div>
    </div>
  )
}

function DuplicateRow({ item }: { item: ReviewItem }) {
  const record = useRecordRule()
  const dismiss = useDismiss()
  const other = item.other!
  // Merge into the concept with more artworks; its name and identity are the ones people know.
  const [keep, absorb] = item.concept.artworkCount >= other.artworkCount ? [item.concept, other] : [other, item.concept]
  return (
    <div className="flex items-start justify-between gap-3 border-b py-3 last:border-0">
      <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
        <ConceptCell c={item.concept} />
        <ConceptCell c={other} />
      </div>
      <div className="w-40 shrink-0 text-xs text-slate-500">
        {item.detail.reason === 'same name' ? 'Same name' : `Similarity ${item.detail.similarity?.toFixed(2)}`}
        {item.detail.sharedArtworks != null && <div>{item.detail.sharedArtworks} artworks carry both</div>}
      </div>
      <div className="flex shrink-0 flex-col gap-1.5">
        <Button size="sm" disabled={record.isPending} onClick={() => record.mutate({ action: 'merge', ucid: keep.ucid, ucids: [absorb.ucid] })}>
          <GitMerge className="mr-1.5 h-3.5 w-3.5" /> Merge
        </Button>
        <Button size="sm" variant="outline" onClick={() => dismiss.mutate([{ kind: item.kind, ucid: item.concept.ucid, otherUcid: other.ucid }])}>
          <X className="mr-1.5 h-3.5 w-3.5" /> Different
        </Button>
      </div>
    </div>
  )
}

function UncategorisedRow({ item, selected, onToggle }: { item: ReviewItem; selected: Selection; onToggle: (id: string) => void }) {
  const curateMany = useCurateMany()
  const suggested = item.detail.suggested ?? item.concept.suggestedCategory ?? null
  const [choice, setChoice] = useState<string>(suggested ?? '')
  useEffect(() => setChoice(suggested ?? ''), [suggested])
  return (
    <div className="flex items-center gap-3 border-b py-2 last:border-0">
      <SelectBox id={item.concept.ucid} selected={selected} onToggle={onToggle} />
      <div className="min-w-0 flex-1">
        <Link to={`/app/knowledge/${item.concept.ucid}`} className="font-medium hover:underline">{item.concept.name}</Link>
        <div className="truncate text-xs text-slate-500">
          {item.concept.artworkCount.toLocaleString()} artworks
          {item.concept.memberNames.length > 1 && ` · also ${item.concept.memberNames.filter((m) => m !== item.concept.name).slice(0, 3).join(', ')}`}
        </div>
      </div>
      <div className="w-36 text-xs">
        {suggested ? (
          <span className="inline-flex items-center gap-1 text-slate-600">
            <Sparkles className="h-3 w-3 text-violet-500" /> {suggested}
            {item.detail.confidence != null && <span className="text-slate-400">{Math.round(item.detail.confidence * 100)}%</span>}
          </span>
        ) : (
          <span className="text-slate-400">no suggestion yet</span>
        )}
      </div>
      <Select value={choice || undefined} onValueChange={setChoice}>
        <SelectTrigger className="h-8 w-36">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        disabled={!choice || curateMany.isPending}
        onClick={() => curateMany.mutate([{ ucid: item.concept.ucid, change: { category: choice, fromSuggestion: choice === suggested } }])}
      >
        Set
      </Button>
    </div>
  )
}

function SimpleRow({ item, selected, onToggle }: { item: ReviewItem; selected: Selection; onToggle: (id: string) => void }) {
  const curateMany = useCurateMany()
  const dismiss = useDismiss()
  return (
    <div className="flex items-center gap-3 border-b py-2 last:border-0">
      <SelectBox id={item.concept.ucid} selected={selected} onToggle={onToggle} />
      <div className="min-w-0 flex-1">
        <ConceptCell c={item.concept} />
      </div>
      {item.kind === 'generic' && (
        <div className="w-44 text-right text-xs text-slate-500">
          on {Math.round((item.detail.share ?? 0) * 100)}% of artworks · weight <WeightLabel weight={item.detail.specificity} />
        </div>
      )}
      <div className="flex shrink-0 gap-1.5">
        {item.kind === 'generic' && (
          <Button size="sm" disabled={curateMany.isPending} onClick={() => curateMany.mutate([{ ucid: item.concept.ucid, change: { weight: 0.1 } }])}>
            Mark generic
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={curateMany.isPending} onClick={() => curateMany.mutate([{ ucid: item.concept.ucid, change: { hidden: true } }])}>
          <EyeOff className="mr-1 h-3.5 w-3.5" /> Hide
        </Button>
        <Button size="sm" variant="ghost" onClick={() => dismiss.mutate([{ kind: item.kind, ucid: item.concept.ucid }])}>
          Dismiss
        </Button>
      </div>
    </div>
  )
}

export function ReviewPage() {
  const [params, setParams] = useSearchParams()
  const kind = (params.get('kind') as ReviewKind) || 'generic'
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Selection>(new Set())
  const { data: summary } = useReviewSummary()
  const { data, isLoading, isFetching } = useReviewItems(kind, offset, PAGE)
  const curateMany = useCurateMany()
  const dismiss = useDismiss()
  const suggest = useSuggestCategories()
  const refresh = useRefreshReview()
  const meta = KINDS.find((k) => k.kind === kind)!

  useEffect(() => {
    setOffset(0)
    setSelected(new Set())
  }, [kind])
  useEffect(() => setSelected(new Set()), [data])

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }
  const items = data?.data ?? []
  const bulkable = kind === 'generic' || kind === 'thin' || kind === 'uncategorised'
  const chosen = items.filter((i) => selected.has(i.concept.ucid))
  const total = data?.total ?? 0

  return (
    <KnowledgeFrame
      title="Review"
      description={
        <>
          A worklist of concepts that probably need a curator, ranked so the ones affecting the most
          artworks come first. Recomputed after every nightly sync
          {summary?.computedAt && <> (last {formatDistanceToNow(new Date(summary.computedAt), { addSuffix: true })})</>}.
          Dismissed items stay dismissed.
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            onClick={() => setParams({ kind: k.kind })}
            className={`rounded-full border px-3 py-1 text-sm ${kind === k.kind ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
          >
            {k.label}
            <span className="ml-1.5 tabular-nums opacity-60">{(summary?.counts[k.kind] ?? 0).toLocaleString()}</span>
          </button>
        ))}
        <Button size="sm" variant="ghost" className="ml-auto" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} /> Recompute
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm">{meta.label}</CardTitle>
              <CardDescription>
                {meta.what} <span className="text-slate-700">{meta.fix}</span>
              </CardDescription>
            </div>
            {kind === 'uncategorised' && (
              <Button size="sm" variant="outline" disabled={suggest.isPending} onClick={() => suggest.mutate(300)}>
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                {suggest.isPending ? 'Asking Gemini…' : 'Suggest categories (next 300)'}
              </Button>
            )}
          </div>
          {bulkable && items.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2 text-sm">
              <Checkbox
                checked={chosen.length === items.length}
                onCheckedChange={(v) => setSelected(v ? new Set(items.map((i) => i.concept.ucid)) : new Set())}
                aria-label="Select all on this page"
              />
              <span className="text-slate-500">{chosen.length ? `${chosen.length} selected` : 'Select all on this page'}</span>
              {chosen.length > 0 && kind === 'generic' && (
                <Button size="sm" disabled={curateMany.isPending} onClick={() => curateMany.mutate(chosen.map((i) => ({ ucid: i.concept.ucid, change: { weight: 0.1 } })))}>
                  Mark generic
                </Button>
              )}
              {chosen.length > 0 && kind === 'uncategorised' && (
                <Button
                  size="sm"
                  disabled={curateMany.isPending || !chosen.some((i) => i.detail.suggested)}
                  onClick={() =>
                    curateMany.mutate(
                      chosen.filter((i) => i.detail.suggested).map((i) => ({ ucid: i.concept.ucid, change: { category: i.detail.suggested!, fromSuggestion: true } }))
                    )
                  }
                >
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Accept {chosen.filter((i) => i.detail.suggested).length} suggestions
                </Button>
              )}
              {chosen.length > 0 && kind !== 'uncategorised' && (
                <Button size="sm" variant="outline" disabled={curateMany.isPending} onClick={() => curateMany.mutate(chosen.map((i) => ({ ucid: i.concept.ucid, change: { hidden: true } })))}>
                  Hide
                </Button>
              )}
              {chosen.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(chosen.map((i) => ({ kind, ucid: i.concept.ucid })))}>
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className={isFetching ? 'opacity-70' : undefined}>
          {isLoading && <Skeleton className="h-64 w-full" />}
          {!isLoading && items.length === 0 && <p className="py-8 text-center text-sm text-slate-500">Nothing to review here.</p>}
          {items.map((item) => {
            // Switch on the item's own kind: while a new list loads, the previous list's items are shown.
            const key = `${item.kind}:${item.concept.ucid}:${item.other?.ucid ?? ''}`
            if (item.kind === 'suspect_merge') return <SuspectRow key={key} item={item} />
            if (item.kind === 'duplicate' && item.other) return <DuplicateRow key={key} item={item} />
            if (item.kind === 'uncategorised') return <UncategorisedRow key={key} item={item} selected={selected} onToggle={toggle} />
            return <SimpleRow key={key} item={item} selected={selected} onToggle={toggle} />
          })}
          {total > PAGE && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
                <Button size="sm" variant="outline" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </KnowledgeFrame>
  )
}
